// FTEDN Team - Shipment Management Page
import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import './ShipmentMgmt.css';

interface Shipment {
  id: string;
  productName?: string;
  userName?: string;
  creatorName?: string;
  status?: string;
  carrier?: string;
  trackingNumber?: string;
}

function getItems<T>(response: unknown): T[] {
  const payload = response as { data?: T[] };
  return Array.isArray(payload?.data) ? payload.data : [];
}

export function ShipmentMgmtPage() {
  const { get, patch } = useApi();
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actingId, setActingId] = useState<string | null>(null);

  const loadShipments = useCallback(async () => {
    setError('');
    try {
      setShipments(getItems<Shipment>(await get('/shipments')));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shipments');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    void loadShipments();
  }, [loadShipments]);

  async function markDelivered(shipment: Shipment) {
    setActingId(shipment.id);
    setError('');
    try {
      await patch(`/shipments/${shipment.id}/status`, { status: 'delivered' });
      await loadShipments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update shipment');
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="mgmt-page">
      <div className="mgmt-header">
        <h1>Shipments</h1>
        <p className="mgmt-subtitle">Track all beta product shipments</p>
        <div className="mgmt-stats">
          <span className="stat-chip">{shipments.length} Shipments</span>
          <span className="stat-chip approved">{shipments.filter((shipment) => shipment.status === 'delivered').length} Delivered</span>
        </div>
      </div>
      {error && <div className="mgmt-error" role="alert">{error}</div>}
      <div className="shipment-list">
        {loading && <div className="mgmt-empty">Loading shipments...</div>}
        {!loading && shipments.length === 0 && <div className="mgmt-empty">No shipments yet.</div>}
        {shipments.map((shipment) => {
          const productName = shipment.productName || 'Shipment';
          const status = shipment.status || 'created';
          return (
            <div key={shipment.id} className="shipment-row">
              <div className="shipment-row-info">
                <strong>{productName}</strong>
                <span>{shipment.userName || shipment.creatorName || 'Creator'} - {shipment.carrier || 'carrier pending'} {shipment.trackingNumber || ''}</span>
              </div>
              <span className={`status-badge ${status}`}>{status}</span>
              {status !== 'delivered' && (
                <div className="mgmt-actions">
                  <button className="action-btn approve" disabled={actingId === shipment.id} onClick={() => void markDelivered(shipment)} aria-label={`Mark ${productName} delivered`}>
                    Mark delivered
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
